import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HubShell } from "../components/hub/shell";
import AddEditUserModal from "../components/AddEditUserModal";
import UserProjectAssignmentModal from "../components/UserProjectAssignmentModal";
import { useNotification } from "../contexts/NotificationContext";
import { useActiveRole } from "../hooks/useActiveRole";
import { useView } from "../contexts/ViewContext";
import { ROLE_NAME_TO_NUMERIC } from "../contexts/AuthContext";
import {
  getAllUsers,
  deleteUser,
  listOrgMembers,
} from "../api";

const UserManagementPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showConfirm } = useNotification();
  const username = localStorage.getItem("username") || "admin";
  // Multi-Org Membership — Epic 2 (S2.5): migrate off the single flattened
  // localStorage role. platform_admin is a USER-level trait (keeps the full role
  // filter set); otherwise the active-org role decides which filters show.
  const { isPlatformAdmin, role: activeRole } = useActiveRole();
  const currentUserRole = isPlatformAdmin ? 0 : (activeRole ?? 3);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  
  // Sort
  const [sortBy, setSortBy] = useState("name"); // name, date

  // Modals
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showProjectAssignmentModal, setShowProjectAssignmentModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Mobile card action menu
  const [openMenuUserId, setOpenMenuUserId] = useState(null);

  useEffect(() => {
    if (openMenuUserId === null) return;
    const close = () => setOpenMenuUserId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuUserId]);

  const roleLabels = {
    0: "Super Admin",
    1: "Admin",
    2: "Manager",
    3: "Client",
  };

  const roleColors = {
    0: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    1: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    2: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    3: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  };

  // Get available role filter options based on current user's role
  const getAvailableRoleFilters = () => {
    if (currentUserRole === 0) {
      // Super Admin can see: Admin, Manager, Client (not other super admins)
      return [
        { value: "all", label: "All Roles" },
        { value: "1", label: "Admin" },
        { value: "2", label: "Manager" },
        { value: "3", label: "Client" },
      ];
    } else if (currentUserRole === 1) {
      // Admin can see: Admin, Manager, Client
      return [
        { value: "all", label: "All Roles" },
        { value: "1", label: "Admin" },
        { value: "2", label: "Manager" },
        { value: "3", label: "Client" },
      ];
    } else if (currentUserRole === 2) {
      // Manager can only see: Client
      return [
        { value: "all", label: "All Roles" },
        { value: "3", label: "Client" },
      ];
    }
    return [{ value: "all", label: "All Roles" }];
  };

  const availableRoleFilters = getAvailableRoleFilters();

  // ── Multi-Org Membership — Epic 2 (S2.5): role shown is the user's role IN
  // the selected org, not a single flattened primary role. When an org is
  // selected we fetch its members (fn_list_org_members) and join by user_id;
  // users who aren't members of the selected org are marked "Not in this org".
  // When no org is selected (platform_admin comparing across orgs) the list
  // falls back to the row's primary role — unchanged behavior.
  const { selectedOrgId } = useView();
  const [orgMemberMap, setOrgMemberMap] = useState({}); // { [userId]: { role_name, role_id, status } }
  useEffect(() => {
    if (!selectedOrgId) { setOrgMemberMap({}); return; }
    let cancelled = false;
    listOrgMembers(selectedOrgId)
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r?.data) ? r.data : [];
        const map = {};
        for (const m of rows) {
          const uid = m.user_id || m.id;
          if (uid) map[String(uid)] = { role_name: m.role_name, role_id: m.role_id, status: m.status };
        }
        setOrgMemberMap(map);
      })
      .catch(() => { if (!cancelled) setOrgMemberMap({}); });
    return () => { cancelled = true; };
  }, [selectedOrgId]);

  // Role-name → {label, color} for the reconciled badge. Reuses the legacy
  // 0/1/2/3 palette so the list reads consistently before/after org selection.
  const orgRoleMeta = {
    platform_admin: { label: "Super Admin", color: roleColors[0] },
    org_owner: { label: "Owner", color: roleColors[1] },
    org_admin: { label: "Admin", color: roleColors[1] },
    org_analyst: { label: "Analyst", color: roleColors[2] },
    org_viewer: { label: "Viewer", color: roleColors[3] },
    org_scoped: { label: "Scoped", color: roleColors[3] },
  };
  const NOT_IN_ORG = { label: "Not in this org", color: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" };

  const resolveRowRole = (user) => {
    if (selectedOrgId) {
      const m = orgMemberMap[String(user.id)];
      if (m) return { code: ROLE_NAME_TO_NUMERIC[m.role_name] ?? 99, roleName: m.role_name, inOrg: true };
      return { code: null, roleName: null, inOrg: false };
    }
    return { code: user.role, roleName: null, inOrg: true };
  };

  const renderRoleBadge = (user, extraClass = "") => {
    const r = resolveRowRole(user);
    if (selectedOrgId && !r.inOrg) {
      return (
        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${NOT_IN_ORG.color} ${extraClass}`}>
          {NOT_IN_ORG.label}
        </span>
      );
    }
    if (selectedOrgId && r.inOrg) {
      const meta = orgRoleMeta[r.roleName] || { label: r.roleName || "Unknown", color: roleColors[3] };
      return (
        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${meta.color} ${extraClass}`}>
          {meta.label}
        </span>
      );
    }
    return (
      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${roleColors[user.role] || roleColors[3]} ${extraClass}`}>
        {roleLabels[user.role] || "Unknown"}
      </span>
    );
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getAllUsers();
      const userList = response?.data ?? response ?? [];
      setUsers(Array.isArray(userList) ? userList : []);
    } catch (err) {
      setError(err?.message || "Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Advanced filtering and sorting
  const filteredUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        (user.display_name || '').toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower);

      // Role filter follows the reconciled role-in-org (Epic 2, S2.5): when an
      // org is selected, a user not in that org has no role here and won't match
      // any role filter except "all".
      const rowRole = resolveRowRole(user);
      const matchesRole = roleFilter === "all" || (rowRole.code != null && String(rowRole.code) === String(roleFilter));

      // Status filter
      const matchesStatus = statusFilter === "all" || String(user.status) === String(statusFilter);

      // Subscription filter
      const matchesSubscription =
        subscriptionFilter === "all" ||
        (subscriptionFilter === "chat" && user.is_chat_subscribed) ||
        (subscriptionFilter === "voice" && user.is_voice_subscribed) ||
        (subscriptionFilter === "none" && !user.is_chat_subscribed && !user.is_voice_subscribed);

      return matchesSearch && matchesRole && matchesStatus && matchesSubscription;
    });

    // Sort based on selected option
    return filtered.sort((a, b) => {
      if (sortBy === "name") {
        // Sort alphabetically by first_name, then last_name
        const aFirst = (a.first_name || '').toLowerCase();
        const bFirst = (b.first_name || '').toLowerCase();
        const aLast = (a.last_name || '').toLowerCase();
        const bLast = (b.last_name || '').toLowerCase();

        if (aFirst < bFirst) return -1;
        if (aFirst > bFirst) return 1;
        if (aLast < bLast) return -1;
        if (aLast > bLast) return 1;
        return 0;
      } else if (sortBy === "date-newest") {
        // Sort by creation date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === "date-oldest") {
        // Sort by creation date (oldest first)
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      return 0;
    });
  }, [users, searchQuery, roleFilter, statusFilter, subscriptionFilter, sortBy, selectedOrgId, orgMemberMap]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, statusFilter, subscriptionFilter, sortBy]);

  const handleDeleteUser = async (user) => {
    if (!user) return;

    const confirmed = await showConfirm({
      title: 'Delete User Account',
      message: `Are you sure you want to delete ${user.first_name || user.display_name || user.email}?\n\nThis action cannot be undone and will also delete all associated projects and data.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700',
    });

    if (!confirmed) return;

    try {
      const resp = await deleteUser(user.id);
      const label = user.first_name || user.display_name || user.email;
      if (resp?.warning) {
        // Partial success: profile deactivated but auth.users delete failed.
        // Refresh the list anyway — the profile deactivation takes them out.
        showWarning('User Removed with Warning', `${label} was deactivated, but backend couldn't fully clean up the login record. They won't show up here anymore, but their auth entry still exists — flag to backend if this persists.`);
      } else {
        showSuccess('User Deleted', `${label} has been successfully deleted.`);
      }
      await fetchUsers();
      setSelectedUser(null);
    } catch (err) {
      const errorMessage = err?.message || err?.error || "Failed to delete user";
      showError('Deletion Failed', errorMessage);
    }
  };

  return (
    <HubShell>
      <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {currentUserRole === 0 && "Super Admin: Manage admins, managers, and clients"}
              {currentUserRole === 1 && "Admin: Manage admins, managers, and clients"}
              {currentUserRole === 2 && "Manager: Manage client accounts"}
            </p>
          </div>
          <button
            onClick={() => setShowAddUserModal(true)}
            className="self-end px-4 py-2 bg-[#32B1FF] hover:bg-[#259ae6] text-white rounded-lg font-semibold shadow-sm transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 4v16m8-8H4" />
            </svg>
            Add New User
          </button>
        </div>

        {/* Filters & Sort */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, username..."
                  className="w-full border dark:border-gray-700 rounded-lg px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              >
                {availableRoleFilters.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              >
                <option value="all">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            {/* Subscription Filter */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Subscription
              </label>
              <select
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              >
                <option value="all">All</option>
                <option value="chat">Chat Only</option>
                <option value="voice">Voice Only</option>
                <option value="none">No Subscription</option>
              </select>
            </div>
          </div>

          {/* Sort Options */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5 order-1">
              <svg className="w-4 h-4 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
              </svg>
              Sort by:
            </label>
            <div className="flex gap-2 w-full sm:w-auto order-3 sm:order-2">
              <button
                onClick={() => setSortBy("name")}
                className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  sortBy === "name"
                    ? "bg-[#32B1FF] text-white"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                Name A-Z
              </button>
              <button
                onClick={() => setSortBy("date-newest")}
                className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  sortBy === "date-newest"
                    ? "bg-[#32B1FF] text-white"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                Newest First
              </button>
              <button
                onClick={() => setSortBy("date-oldest")}
                className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  sortBy === "date-oldest"
                    ? "bg-[#32B1FF] text-white"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                Oldest First
              </button>
            </div>

            {/* Results count */}
            <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto order-2 sm:order-3">
              {filteredUsers.length} {filteredUsers.length === 1 ? "user" : "users"}
            </span>
          </div>

          {/* Clear Filters */}
          {(searchQuery || roleFilter !== "all" || statusFilter !== "all" || subscriptionFilter !== "all") && (
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => {
                  setSearchQuery("");
                  setRoleFilter("all");
                  setStatusFilter("all");
                  setSubscriptionFilter("all");
                  setSortBy("name");
                }}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear all filters & reset sort
              </button>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading users...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600 dark:text-red-400">{error}</div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No users found matching your filters.
            </div>
          ) : (
            <>
              {/* Mobile card view (<500px) */}
              <div className="min-[942px]:hidden divide-y dark:divide-gray-700">
                {paginatedUsers.map((user) => (
                  <div key={user.id} className="p-6 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#32B1FF] text-white flex items-center justify-center font-semibold shrink-0">
                        {(user.first_name?.[0] || user.display_name?.[0] || user.email?.[0] || "U").toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100 break-words">
                          {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.display_name || user.email}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 break-all">{user.email}</div>
                      </div>
                      <div className="relative shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuUserId(openMenuUserId === user.id ? null : user.id);
                          }}
                          className="p-2 -mr-2 -mt-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Actions"
                          aria-label="Actions"
                          aria-expanded={openMenuUserId === user.id}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="5" cy="12" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="19" cy="12" r="2" />
                          </svg>
                        </button>
                        {openMenuUserId === user.id && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="absolute right-0 top-9 z-20 w-44 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg shadow-lg py-1"
                          >
                            <button
                              onClick={() => {
                                setOpenMenuUserId(null);
                                setSelectedUser(user);
                                setShowProjectAssignmentModal(true);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                              <svg className="w-4 h-4 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
                              </svg>
                              Manage Projects
                            </button>
                            <button
                              onClick={() => {
                                setOpenMenuUserId(null);
                                setSelectedUser(user);
                                setShowEditUserModal(true);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                              <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Edit User
                            </button>
                            {user.role !== 0 && (
                              <button
                                onClick={() => {
                                  setOpenMenuUserId(null);
                                  handleDeleteUser(user);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                                Delete User
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/40">
                        <span className="font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 shrink-0">Role</span>
                        <span
                          className={`ml-auto inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            roleColors[user.role] || roleColors[3]
                          }`}
                        >
                          {roleLabels[user.role] || "Unknown"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/40">
                        <span className="font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 shrink-0">Status</span>
                        <span
                          className={`ml-auto inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            user.status === 1
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                          }`}
                        >
                          {user.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/40">
                        <span className="font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 shrink-0">Subscriptions</span>
                        <div className="ml-auto flex flex-wrap justify-end gap-1.5">
                          {user.is_chat_subscribed && (
                            <span className="inline-flex px-2 py-0.5 items-center text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                              Chat
                            </span>
                          )}
                          {user.is_voice_subscribed && (
                            <span className="inline-flex px-2 py-0.5 items-center text-xs font-medium rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                              Voice
                            </span>
                          )}
                          {!user.is_chat_subscribed && !user.is_voice_subscribed && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">None</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Table view (>=500px) */}
              <div className="hidden min-[942px]:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Subscriptions
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-700">
                    {paginatedUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#32B1FF] text-white flex items-center justify-center font-semibold">
                              {(user.first_name?.[0] || user.display_name?.[0] || user.email?.[0] || "U").toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.display_name || user.email}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                              roleColors[user.role] || roleColors[3]
                            }`}
                          >
                            {roleLabels[user.role] || "Unknown"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                              user.status === 1
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                            }`}
                          >
                            {user.status === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1">
                            {user.is_chat_subscribed && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                Chat
                              </span>
                            )}
                            {user.is_voice_subscribed && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                Voice
                              </span>
                            )}
                            {!user.is_chat_subscribed && !user.is_voice_subscribed && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {/* Icons (>=1055px) */}
                          <div className="hidden min-[1055px]:flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowProjectAssignmentModal(true);
                              }}
                              className="p-2 text-[#32B1FF] hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Manage Projects"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowEditUserModal(true);
                              }}
                              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              title="Edit User"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            {user.role !== 0 && (
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                title="Delete User"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            )}
                          </div>

                          {/* Kebab menu (<1055px) */}
                          <div className="min-[1055px]:hidden flex items-center justify-end">
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuUserId(openMenuUserId === user.id ? null : user.id);
                                }}
                                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                title="Actions"
                                aria-label="Actions"
                                aria-expanded={openMenuUserId === user.id}
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                  <circle cx="5" cy="12" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="19" cy="12" r="2" />
                                </svg>
                              </button>
                              {openMenuUserId === user.id && (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  className="absolute right-0 top-9 z-20 w-44 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg shadow-lg py-1"
                                >
                                  <button
                                    onClick={() => {
                                      setOpenMenuUserId(null);
                                      setSelectedUser(user);
                                      setShowProjectAssignmentModal(true);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                  >
                                    <svg className="w-4 h-4 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
                                    </svg>
                                    Manage Projects
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOpenMenuUserId(null);
                                      setSelectedUser(user);
                                      setShowEditUserModal(true);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                  >
                                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    Edit User
                                  </button>
                                  {user.role !== 0 && (
                                    <button
                                      onClick={() => {
                                        setOpenMenuUserId(null);
                                        handleDeleteUser(user);
                                      }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                      </svg>
                                      Delete User
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredUsers.length > itemsPerPage && (
                <div className="px-6 py-4 border-t dark:border-gray-700 flex flex-col-reverse gap-3 min-[650px]:flex-row min-[650px]:items-center min-[650px]:justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 dark:text-gray-400">Show</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border dark:border-gray-700 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-auto">
                      Showing {(currentPage - 1) * itemsPerPage + 1}-
                      {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center justify-between min-[650px]:justify-end gap-1 w-full min-[650px]:w-auto min-[650px]:ml-auto">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="max-[649px]:min-w-[100px] px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100"
                    >
                      Previous
                    </button>
                    <div className="hidden min-[650px]:flex flex-wrap items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          return (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          );
                        })
                        .map((page, idx, arr) => {
                          if (idx > 0 && page - arr[idx - 1] > 1) {
                            return (
                              <React.Fragment key={`ellipsis-${page}`}>
                                <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
                                <button
                                  onClick={() => setCurrentPage(page)}
                                  className={`px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm ${
                                    currentPage === page
                                      ? "bg-[#32B1FF] text-white border-[#32B1FF]"
                                      : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          }
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm ${
                                currentPage === page
                                  ? "bg-[#32B1FF] text-white border-[#32B1FF]"
                                  : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                    </div>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="max-[649px]:min-w-[100px] px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <AddEditUserModal
        open={showAddUserModal || showEditUserModal}
        onClose={() => {
          setShowAddUserModal(false);
          setShowEditUserModal(false);
          setSelectedUser(null);
        }}
        onSuccess={fetchUsers}
        user={showEditUserModal ? selectedUser : null}
      />

      {/* User Project Assignment Modal */}
      <UserProjectAssignmentModal
        open={showProjectAssignmentModal}
        onClose={() => {
          setShowProjectAssignmentModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSuccess={() => {
          showSuccess("Project assignments updated successfully!");
          setShowProjectAssignmentModal(false);
          setSelectedUser(null);
        }}
      />
    </HubShell>
  );
};

export default UserManagementPage;

